import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const isPawn = (c) => (c || "").toLowerCase().includes("pawn");
  const cats = useMemo(() => ["all", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = useMemo(() => {
    return products.filter((p) =>
      // Hide "Pawn Shop" items from the default ALL view — only show them when that
      // exact category is selected. Search still respects the category filter, so a
      // search across "all" won't surface pawn items either.
      (cat === "all" ? !isPawn(p.category) : p.category === cat) &&
      (q === "" || p.name.toLowerCase().includes(q.toLowerCase()))
    );
  }, [products, q, cat]);
  return (
    <div className="mx-auto max-w-7xl px-5 py-12 md:px-8 md:py-16">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">Shop</span>
          <h1 className="mt-2 font-display text-4xl font-bold text-slate-900 md:text-5xl wavy">Pool gear, curated.</h1>
          <p className="mt-2 max-w-md text-slate-600">Chemicals, tools, equipment, and seasonal services — chosen by pros, ready for your backyard.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9" data-testid="shop-search-input" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-44" data-testid="shop-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c[0].toUpperCase() + c.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => (
          <article key={p.id} className="group overflow-hidden rounded-2xl border border-cyan-100 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg" data-testid={`product-card-${p.id}`}>
            <Link to={`/shop/${p.id}`} className="block">
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                ) : <div className="grid h-full place-items-center text-slate-400">No image</div>}
                <div className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-cyan-700 backdrop-blur">{p.category}</div>
              </div>
            </Link>
            <div className="p-5">
              <h3 className="font-display text-lg font-bold text-slate-900 line-clamp-1">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-display text-xl font-bold text-cyan-700">{fmt(p.price_cents)}</span>
                <Button onClick={() => add(p)} className="btn-aqua !px-4 !py-2 text-sm" data-testid={`product-add-${p.id}`}>
                  <ShoppingBag className="h-4 w-4" /> Add
                </Button>
              </div>
            </div>
          </article>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
            No products match your search.
          </div>
        )}
      </div>
    </div>
  );
}
